/**
 * Draft auto-save coordinator for the Visual Automation Builder.
 *
 * Single path for ALL draft persistence (auto save, manual Save Draft,
 * the Save-failed Retry button, and the publish/simulate pre-save), so
 * the discipline below covers every write the editor makes:
 *
 * - DEBOUNCE: a dirty snapshot is saved after ~1200ms of quiet
 *   (trailing edge); each new edit re-arms the timer.
 * - ONE IN FLIGHT: at most one save request per editor instance. Edits
 *   landing during a flight coalesce into exactly ONE follow-up save
 *   carrying the LATEST snapshot, issued when the flight settles.
 * - LATEST WINS: snapshots carry a monotonically increasing sequence.
 *   A completion advances `persistedSeq` only to the sequence of the
 *   snapshot it actually carried, and "Saved" is computed as
 *   persistedSeq === editSeq — so a response for an older snapshot can
 *   never mark the editor Saved while newer edits are unsaved, and the
 *   full-graph-replace payload guarantees the newest snapshot is the
 *   last one written.
 * - RETRY: a rejected attempt is retried with backoff up to
 *   `maxRetries` times per cycle (each retry re-reads the latest
 *   snapshot); the cycle then rests at "Save failed". Further edits,
 *   `flush()` and `retry()` start a fresh cycle with a fresh budget.
 *   Retries cannot duplicate operations: the payload is a whole-draft
 *   replace, and a create's returned id is adopted (`resolvedId`)
 *   before any follow-up, so a retry PUTs instead of POSTing again.
 * - GENERATIONS: `reset()` (opening another flow / a new draft) bumps
 *   the generation; completions from an older generation are ignored
 *   for status/id purposes, so a late save for a flow the user already
 *   left can never relabel the flow now on screen.
 *
 * The coordinator is framework-free; the manager owns the instance and
 * renders `status`: 'saving' while a save is pending (debounce) or in
 * flight with unsaved changes, 'saved' ONLY when the persisted snapshot
 * equals the current graph, 'failed' after an exhausted cycle.
 */

export type DraftSaveStatus = 'saved' | 'saving' | 'failed';

export interface DraftSnapshot {
  /** Builder session this snapshot belongs to (manager-owned counter). */
  sessionId: number;
  /** Automation id if the draft exists server-side yet, else null. */
  automationId: string | null;
  /**
   * Content identity: JSON of the draft's content fields. A snapshot
   * whose key equals the persisted key is by definition already saved.
   */
  key: string;
  name: string;
  description: string;
  category: string;
  nodes: unknown[];
  edges: unknown[];
  executionSettings: { maxRetries: number; errorHandling: string };
}

export interface DraftSaveCoordinatorOptions {
  /** Persists one snapshot; resolves with the automation id. */
  save: (snapshot: DraftSnapshot) => Promise<string>;
  debounceMs?: number;
  maxRetries?: number;
  backoffMs?: number[];
  onStatus?: (status: DraftSaveStatus) => void;
  /** Fired when a snapshot becomes the persisted state (manager re-anchors its baseline). */
  onSaved?: (snapshot: DraftSnapshot, id: string) => void;
}

const DEFAULT_DEBOUNCE_MS = 1200;
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_BACKOFF_MS = [600, 1800];

export class DraftSaveCoordinator {
  private readonly saveFn: (snapshot: DraftSnapshot) => Promise<string>;
  private readonly debounceMs: number;
  private readonly maxRetries: number;
  private readonly backoffMs: number[];
  private readonly onStatus?: (status: DraftSaveStatus) => void;
  private readonly onSaved?: (snapshot: DraftSnapshot, id: string) => void;

  private generation = 0;
  private editSeq = 0;
  private persistedSeq = 0;
  private latest: DraftSnapshot | null = null;
  private persistedKey: string | null = null;
  private resolvedId: string | null = null;
  private status: DraftSaveStatus = 'saved';
  private failed = false;
  private lastError: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private cyclePromise: Promise<void> | null = null;

  constructor(options: DraftSaveCoordinatorOptions) {
    this.saveFn = options.save;
    this.debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    this.maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.backoffMs = options.backoffMs ?? DEFAULT_BACKOFF_MS;
    this.onStatus = options.onStatus;
    this.onSaved = options.onSaved;
  }

  getStatus(): DraftSaveStatus {
    return this.status;
  }

  getResolvedId(): string | null {
    return this.resolvedId;
  }

  getLastError(): string | null {
    return this.lastError;
  }

  /** True while any edit is not yet persisted (debounce, flight, or failed). */
  isDirty(): boolean {
    return this.timer !== null || this.editSeq > this.persistedSeq;
  }

  /**
   * Anchor the coordinator to a freshly loaded (or brand-new) draft:
   * the snapshot IS the persisted state, so nothing is dirty and no
   * save may fire for it.
   */
  reset(snapshot: DraftSnapshot): void {
    this.generation += 1;
    this.clearTimer();
    this.latest = snapshot;
    this.persistedKey = snapshot.key;
    this.resolvedId = snapshot.automationId;
    this.editSeq = this.persistedSeq = this.editSeq + 1;
    this.failed = false;
    this.lastError = null;
    this.setStatus('saved');
  }

  /** Record an edit and schedule its save after the debounce quiet period. */
  markDirty(snapshot: DraftSnapshot): void {
    if (snapshot.key === this.persistedKey) {
      // The current content IS the persisted content (e.g. a no-op Auto
      // Align, or an edit reverted before any save landed). Align the
      // sequence so no save is owed for it — unless a save is in flight
      // for different content, in which case this snapshot must queue
      // behind it so the server converges to what's on screen.
      this.latest = { ...snapshot, automationId: this.resolvedId ?? snapshot.automationId };
      if (!this.cyclePromise) {
        this.clearTimer();
        this.editSeq = this.persistedSeq;
        this.failed = false;
        this.setStatus('saved');
      } else {
        this.editSeq += 1;
        this.failed = false;
        this.setStatus('saving');
      }
      return;
    }
    this.latest = { ...snapshot, automationId: this.resolvedId ?? snapshot.automationId };
    this.editSeq += 1;
    this.failed = false;
    this.setStatus('saving');
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.runCycle();
    }, this.debounceMs);
  }

  /**
   * Persist the latest snapshot NOW: cancels the debounce timer, waits
   * out any in-flight cycle, then runs a fresh cycle if still dirty.
   * Resolves true only when the current graph is persisted.
   */
  async flush(): Promise<boolean> {
    this.clearTimer();
    if (this.cyclePromise) {
      await this.cyclePromise;
    }
    if (!this.isDirty()) return true;
    await this.runCycle();
    return !this.isDirty() && !this.failed;
  }

  /** The Save-failed Retry button: a flush with a fresh retry budget. */
  async retry(): Promise<boolean> {
    return this.flush();
  }

  /** Orphan any pending bookkeeping (the manager unmounted). */
  dispose(): void {
    this.generation += 1;
    this.clearTimer();
  }

  private setStatus(next: DraftSaveStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.onStatus?.(next);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private runCycle(): Promise<void> {
    if (this.cyclePromise) return this.cyclePromise;
    const gen = this.generation;
    const p: Promise<void> = (async () => {
      let failures = 0;
      while (gen === this.generation && this.editSeq > this.persistedSeq) {
        const seq = this.editSeq;
        const snap = this.latest;
        if (!snap) break;
        this.setStatus('saving');
        try {
          const id = await this.saveFn(snap);
          if (gen !== this.generation) return;
          this.resolvedId = id;
          this.persistedSeq = Math.max(this.persistedSeq, seq);
          this.persistedKey = snap.key;
          failures = 0;
          this.failed = false;
          this.lastError = null;
          this.onSaved?.(snap, id);
        } catch (err: any) {
          if (gen !== this.generation) return;
          this.lastError = err?.message || 'Save failed';
          failures += 1;
          if (failures > this.maxRetries) {
            this.failed = true;
            this.setStatus('failed');
            return;
          }
          await this.delay(this.backoffMs[Math.min(failures - 1, this.backoffMs.length - 1)]);
        }
      }
      if (gen === this.generation) {
        if (this.editSeq <= this.persistedSeq) {
          // Clean finish. A debounce timer armed by an edit this cycle
          // already persisted (it arrived during a flight and the loop
          // carried it) is a stale duplicate — cancel it so dirtiness
          // and status tell the truth. A timer left by a FAILED cycle
          // is kept: it is the specified re-arm for failed-and-dirty.
          this.clearTimer();
        }
        this.setStatus(
          this.editSeq > this.persistedSeq ? (this.failed ? 'failed' : 'saving') : 'saved'
        );
      }
    })();
    this.cyclePromise = p;
    void p.finally(() => {
      if (this.cyclePromise === p) this.cyclePromise = null;
    });
    return p;
  }
}

/** Content key for a draft snapshot: the fields that define the draft. */
export function draftSnapshotKey(parts: {
  name: string;
  description: string;
  category: string;
  nodes: unknown[];
  edges: unknown[];
  executionSettings: { maxRetries: number; errorHandling: string };
}): string {
  return JSON.stringify({
    name: parts.name,
    description: parts.description,
    category: parts.category,
    nodes: parts.nodes,
    edges: parts.edges,
    executionSettings: parts.executionSettings,
  });
}
